import { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Checkbox,
  FormControlLabel,
  TextField,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import PriceDisplay from "components/PriceDisplay";
import PartnerPriceSection from "layouts/pages/servicelists/PartnerPriceSection";
import iaasService from "services/iaasService";
import Swal from "sweetalert2";
import { AuthContext } from "context";

function CloudServerConfig({ serverType, onBack }) {
  const { user } = useContext(AuthContext);
  const [ram, setRam] = useState(4);
  const [storage, setStorage] = useState(50);
  const [cores, setCores] = useState(2);
  const maxCores = serverType === 'windows' ? 8 : 12;
  const minCores = 2;
  const [backupEnabled, setBackupEnabled] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [endCustomerPrice, setEndCustomerPrice] = useState('');

  // Windows-spezifische States
  const [windowsVersion, setWindowsVersion] = useState("");
  const [rdsLicenses, setRdsLicenses] = useState(0);

  // Linux-spezifische States
  const [distribution, setDistribution] = useState("");

  const showSuccessNotification = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Erfolg!',
      text: message,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK'
    });
  };

  const showErrorNotification = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Fehler',
      text: message,
      confirmButtonColor: '#d33',
      confirmButtonText: 'OK'
    });
  };

  const calculatePrice = () => {
    let basePrice = ram * 2 + Math.ceil(storage / 5) + cores * 5;
    
    if (backupEnabled) basePrice += Math.ceil(storage / 100) * 5;
    
    // Windows-spezifische Preisberechnung
    if (serverType === 'windows') {
      basePrice += 28; // Windows-Lizenz
      basePrice += rdsLicenses * 9; // 15€ pro RDS-Lizenz
    }

    const isPartner = user?.isPartner === true;

    if (isPartner) {
      // 20% Rabatt für Partner
      setOriginalPrice(basePrice);
      setTotalPrice(basePrice * 0.8);
      setDiscountApplied(true);
    } else {
      setTotalPrice(basePrice);
      setOriginalPrice(null);
      setDiscountApplied(false);
    }
  };

  // Stellt sicher, dass die Kerne innerhalb der erlaubten Grenzen liegen
  useEffect(() => {
    if (cores < minCores) {
      setCores(minCores);
    } else if (cores > maxCores) {
      setCores(maxCores);
    }
  }, [serverType, minCores, maxCores, cores]);

  useEffect(() => {
    calculatePrice();
  }, [ram, storage, cores, backupEnabled, rdsLicenses, user?.isPartner]);

  const handleOrder = async () => {
    if (!agreedToTerms) {
      Swal.fire({
        icon: 'warning',
        title: 'Achtung',
        text: 'Bitte stimmen Sie den AGBs zu, um fortzufahren.',
        confirmButtonColor: '#f8bb86',
        confirmButtonText: 'OK'
      });
      return;
    }

    const userId = user?.id;
    
    try {
      const orderDetails = {
        userId,
        ram,
        storage,
        cores,
        backupEnabled,
        totalPrice,
        ...(serverType === 'windows' ? {
          windowsVersion,
          rdsLicenses
        } : {
          distribution
        }),
        endCustomerPrice: endCustomerPrice || undefined
      };
      
      // Füge Rabattinformationen hinzu, wenn ein Rabatt angewendet wurde
      if (discountApplied) {
        orderDetails.priceInfo = {
          originalPrice: originalPrice,
          discountApplied: true
        };
      }

      const response = serverType === 'windows'
        ? await iaasService.orderWindowsServer(orderDetails)
        : await iaasService.orderLinuxServer(orderDetails);

      if (response.status === 200) {
        showSuccessNotification("Ihre Bestellung wurde erfolgreich aufgegeben!");
        setTimeout(() => {
          window.location.href = "/iaas/server";
        }, 2000);
      }
    } catch (error) {
      console.error("Fehler bei der Bestellung:", error);
      showErrorNotification("Es gab ein Problem bei der Bestellung. Bitte versuchen Sie es später erneut.");
    }
  };

  const handleRdsLicensesChange = (event) => {
    const value = parseInt(event.target.value) || 0;
    if (value >= 0) {
      setRdsLicenses(value);
    }
  };

  return (
    <SoftBox p={4}>
      <SoftBox p={3} border="1px solid #e9ecef" borderRadius="lg" bgcolor="#f8f9fa">
        <SoftTypography variant="h4" fontWeight="medium" color="dark" mb={3}>
          {serverType === 'windows' ? 'Windows' : 'Linux'} Server Konfiguration
        </SoftTypography>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SoftBox mb={3} p={3} border="1px solid #e9ecef" borderRadius="lg">
              <SoftTypography variant="h6" mb={2}>RAM (GB)</SoftTypography>
              <Slider
                value={ram}
                min={1}
                max={128}
                step={1}
                onChange={(e, newValue) => setRam(newValue)}
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-thumb': {
                    color: '#8b0000',
                  },
                  '& .MuiSlider-track': {
                    color: '#8b0000',
                  },
                  '& .MuiSlider-rail': {
                    color: '#cccccc',
                  }
                }}
                marks={[
                  { value: 1, label: '1 GB' },
                  { value: 32, label: '32 GB' },
                  { value: 64, label: '64 GB' },
                  { value: 128, label: '128 GB' }
                ]}
              />
              <SoftTypography variant="body2" color="text" textAlign="right" mt={1}>
                Ausgewählt: {ram} GB - Preis: {ram * 2} €
              </SoftTypography>
            </SoftBox>
          </Grid>

          <Grid item xs={12}>
            <SoftBox mb={3} p={3} border="1px solid #e9ecef" borderRadius="lg">
              <SoftTypography variant="h6" mb={2}>Speicherplatz (GB)</SoftTypography>
              <Slider
                value={storage}
                min={25}
                max={1000}
                step={25}
                onChange={(e, newValue) => setStorage(newValue)}
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-thumb': {
                    color: '#8b0000',
                  },
                  '& .MuiSlider-track': {
                    color: '#8b0000',
                  },
                  '& .MuiSlider-rail': {
                    color: '#cccccc',
                  }
                }}
                marks={[
                  { value: 25, label: '25 GB' },
                  { value: 250, label: '250 GB' },
                  { value: 500, label: '500 GB' },
                  { value: 1000, label: '1 TB' }
                ]}
              />
              <SoftTypography variant="body2" color="text" textAlign="right" mt={1}>
                Ausgewählt: {storage} GB - Preis: {Math.ceil(storage / 5)} €
              </SoftTypography>
            </SoftBox>
          </Grid>

          <Grid item xs={12}>
            <SoftBox mb={3} p={3} border="1px solid #e9ecef" borderRadius="lg">
              <SoftTypography variant="h6" mb={2}>CPU-Kerne</SoftTypography>
              <Slider
                value={cores}
                min={minCores}
                max={maxCores}
                step={1}
                onChange={(e, newValue) => setCores(newValue)}
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-thumb': {
                    color: '#8b0000',
                  },
                  '& .MuiSlider-track': {
                    color: '#8b0000',
                  },
                  '& .MuiSlider-rail': {
                    color: '#cccccc',
                  }
                }}
                marks={
                  serverType === 'windows' ? 
                  [
                    { value: 2, label: '2 Kerne' },
                    { value: 4, label: '4 Kerne' },
                    { value: 6, label: '6 Kerne' },
                    { value: 8, label: '8 Kerne' }
                  ] : 
                  [
                    { value: 2, label: '2 Kerne' },
                    { value: 4, label: '4 Kerne' },
                    { value: 8, label: '8 Kerne' },
                    { value: 12, label: '12 Kerne' }
                  ]
                }
              />
              <SoftTypography variant="body2" color="text" textAlign="right" mt={1}>
                Ausgewählt: {cores} Kerne - Preis: {cores * 5} €
              </SoftTypography>
            </SoftBox>
          </Grid>

          {serverType === 'windows' && (
            <>
              <Grid item xs={12}>
                <SoftBox mb={3} p={3} border="1px solid #e9ecef" borderRadius="lg">
                  <SoftTypography variant="h6" mb={2}>Windows Version</SoftTypography>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="windows-version-select-label">Windows Server Version</InputLabel>
                    <Select
                      labelId="windows-version-select-label"
                      id="windows-version-select"
                      value={windowsVersion}
                      onChange={(e) => setWindowsVersion(e.target.value)}
                      label="Windows Server Version"
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 200
                          }
                        }
                      }}
                      componentsProps={{
                        root: {
                          sx: {
                            width: '100%',
                            '& .MuiSelect-select': {
                              width: '100%',
                              cursor: 'pointer'
                            }
                          }
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>Bitte wählen</em>
                      </MenuItem>
                      <MenuItem value="2019">Windows Server 2019</MenuItem>
                      <MenuItem value="2022">Windows Server 2022</MenuItem>
                      <MenuItem value="2025">Windows Server 2025</MenuItem>
                    </Select>
                  </FormControl>
                </SoftBox>
              </Grid>
              <Grid item xs={12}>
                <SoftBox mb={3} p={3} border="1px solid #e9ecef" borderRadius="lg">
                  <SoftTypography variant="h6" mb={2}>RDS CALs</SoftTypography>
                  <TextField
                    fullWidth
                    type="number"
                    label="Anzahl RDS CALs"
                    value={rdsLicenses}
                    onChange={handleRdsLicensesChange}
                    inputProps={{ min: 0 }}
                    variant="outlined"
                    helperText="Geben Sie die gewünschte Anzahl an RDS CALs ein (15 € pro Lizenz)"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#8b0000'
                        }
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#8b0000'
                      }
                    }}
                  />
                  <SoftTypography variant="body2" color="text" mt={1}>
                    Gesamtpreis RDS CALs: {rdsLicenses * 9} €
                  </SoftTypography>
                </SoftBox>
              </Grid>
            </>
          )}

          {serverType === 'linux' && (
            <Grid item xs={12}>
              <SoftBox mb={3} p={3} border="1px solid #e9ecef" borderRadius="lg">
                <SoftTypography variant="h6" mb={2}>Linux Distribution</SoftTypography>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="linux-distribution-select-label">Linux Distribution</InputLabel>
                  <Select
                    labelId="linux-distribution-select-label"
                    id="linux-distribution-select"
                    value={distribution}
                    onChange={(e) => setDistribution(e.target.value)}
                    label="Linux Distribution"
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 200
                        }
                      }
                    }}
                    componentsProps={{
                      root: {
                        sx: {
                          width: '100%',
                          '& .MuiSelect-select': {
                            width: '100%',
                            cursor: 'pointer'
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Bitte wählen</em>
                    </MenuItem>
                    <MenuItem value="ubuntu">Ubuntu Server LTS</MenuItem>
                    <MenuItem value="debian">Debian 12</MenuItem>
                    <MenuItem value="rocky">Rocky Linux 9</MenuItem>
                    <MenuItem value="almalinux">AlmaLinux 9</MenuItem>
                  </Select>
                </FormControl>
              </SoftBox>
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={backupEnabled}
                  onChange={(e) => setBackupEnabled(e.target.checked)}
                  sx={{
                    color: '#8b0000',
                    '&.Mui-checked': {
                      color: '#8b0000',
                    }
                  }}
                />
              }
              label="Tägliches Backup"
            />
          </Grid>

          <Grid item xs={12}>
            <SoftBox p={3} border="1px solid #e9ecef" borderRadius="lg" bgcolor="#f8f9fa">
              <SoftTypography variant="h5" fontWeight="bold" color="dark">
                <PriceDisplay 
                  price={totalPrice} 
                  originalPrice={originalPrice} 
                  discountApplied={discountApplied} 
                  title="Gesamtpreis:"
                />
              </SoftTypography>
              {user?.isPartner && (
                <PartnerPriceSection
                  standardPrice={originalPrice || totalPrice}
                  purchasePrice={totalPrice}
                  endCustomerPrice={endCustomerPrice}
                  onChange={setEndCustomerPrice}
                />
              )}
              <SoftTypography variant="body2" color="text" mt={2}>
                Zusammenfassung:<br />
                - {ram} GB RAM: {ram * 2} €<br />
                - {storage} GB Speicher: {Math.ceil(storage / 5)} €<br />
                - {cores} CPU-Kerne: {cores * 5} €<br />
                {backupEnabled && `- Backup Service: ${Math.ceil(storage / 100) * 5} €\n`}
                {serverType === 'windows' && (
                  <>
                    - Windows Lizenz: 28 €<br />
                    {rdsLicenses > 0 && `- RDS Lizenzen (${rdsLicenses} CALs): ${rdsLicenses * 9} €\n`}
                  </>
                )}
              </SoftTypography>
            </SoftBox>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  sx={{
                    color: '#8b0000',
                    '&.Mui-checked': {
                      color: '#8b0000',
                    }
                  }}
                />
              }
              label={
                <SoftTypography variant="body2">
                  Ich akzeptiere die{" "}
                  <a href="https://uds-solutions.de/agb" target="_blank" rel="noopener noreferrer" style={{ color: '#8b0000' }}>
                    AGBs
                  </a>{" "}
                  und bestelle den Server kostenpflichtig.
                </SoftTypography>
              }
            />
          </Grid>

          <Grid item xs={12}>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftButton 
                variant="outlined"
                onClick={onBack}
                sx={{
                  borderColor: '#8b0000',
                  color: '#8b0000',
                  '&:hover': {
                    borderColor: '#660000',
                    backgroundColor: 'rgba(139,0,0,0.04)'
                  }
                }}
              >
                Zurück
              </SoftButton>
              <SoftButton
                variant="contained"
                onClick={handleOrder}
                disabled={!agreedToTerms || (serverType === 'windows' && !windowsVersion) || (serverType === 'linux' && !distribution)}
                sx={{
                  backgroundColor: '#8b0000',
                  '&:hover': {
                    backgroundColor: '#660000'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc'
                  }
                }}
              >
                Jetzt bestellen
              </SoftButton>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </SoftBox>
  );
}

CloudServerConfig.propTypes = {
  serverType: PropTypes.oneOf(['windows', 'linux']).isRequired,
  onBack: PropTypes.func.isRequired,
};

export default CloudServerConfig;
